/**
 * LuviaRx — modal opened from the "Request Certificate of Analysis"
 * button on ProductDetail. Captures email + optional message and POSTs
 * to /api/coa-requests. Product/variant are auto-attached from props.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Mail, FileCheck2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoaRequestModal({ open, onClose, product, variant }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Pre-fill email when the modal opens for a logged-in user.
  useEffect(() => {
    if (open) {
      setEmail(user?.email || "");
      setMessage("");
      setError("");
      setDone(false);
    }
  }, [open, user]);

  // ESC key closes the modal.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !product) return null;

  const submit = async () => {
    setError("");
    if (!email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coa-requests`, {
        email: email.trim(),
        message: message.trim() || null,
        product_id: product.slug,
        variant_id: variant?.id || null,
        product_name: product.name,
        variant_label: variant?.label || variant?.name || null,
      });
      setDone(true);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Could not submit request. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="coa-request-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-charcoal-600/60 backdrop-blur-sm cursor-default"
      />
      {/* Panel */}
      <div className="relative w-full max-w-md surface-card p-6 md:p-8 shadow-2xl animate-fade-in">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          data-testid="coa-modal-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-charcoal-400 hover:text-charcoal-600 hover:bg-cream-200/70 transition"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        {done ? (
          <div className="text-center py-4" data-testid="coa-modal-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-sage-600 mb-3" strokeWidth={1.8} />
            <p className="overline mb-1">Request sent</p>
            <p className="font-display text-2xl text-charcoal-600 font-bold mb-2">
              We'll be in touch shortly
            </p>
            <p className="text-sm text-charcoal-400 leading-relaxed max-w-xs mx-auto">
              Our team will email the Certificate of Analysis for{" "}
              <span className="font-medium text-charcoal-600">{product.name}</span> to{" "}
              <span className="font-medium text-charcoal-600">{email}</span> within 1 business day.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary mt-6"
              data-testid="coa-modal-done-close"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <FileCheck2 className="w-4 h-4 text-[#8B2252]" strokeWidth={2} />
              <p className="overline">Certificate of Analysis</p>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-charcoal-600 mb-2">
              Request COA for {product.name}
            </h3>
            <p className="text-sm text-charcoal-400 mb-6 leading-relaxed">
              Third-party lab report including identity, HPLC purity, and mass-spectrometry
              verification for the current batch of{" "}
              <span className="font-medium">{variant?.label || variant?.name || product.name}</span>.
            </p>

            <label htmlFor="coa-email" className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2">
              Your email
            </label>
            <input
              id="coa-email"
              data-testid="coa-modal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@researchlab.edu"
              className="w-full px-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none mb-4"
              autoComplete="email"
            />

            <label htmlFor="coa-message" className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2">
              Message <span className="text-charcoal-300 normal-case tracking-normal">(optional — batch #, quantity, etc.)</span>
            </label>
            <textarea
              id="coa-message"
              data-testid="coa-modal-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Any specific batch or additional info that would help us…"
              className="w-full px-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none resize-none mb-4"
            />

            {error && (
              <div
                data-testid="coa-modal-error"
                className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="coa-modal-submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Mail className="w-4 h-4" strokeWidth={2} />
              {submitting ? "Sending request…" : "Send request"}
            </button>

            <p className="mt-4 text-[11px] text-charcoal-400 text-center leading-relaxed">
              We reply within 1 business day. Your info is only used to send the COA and is
              never shared. For Research Use Only.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
